import * as Sentry from "@sentry/react";

import { supabase } from "./supabaseClient";
// Type-only import (erased at build) so this widely-imported lib stays out of recorderActivity's
// static store → supabaseClient chain; the value is pulled in via dynamic import in stopImpersonation.
import type { CoalescingIdleRunner } from "@common/lib/recorderActivity";
import { isRecordingActiveElsewhere } from "@common/lib/recorderPersistence";
import {
    ImpersonationUser,
    ImpersonationSession,
    ImpersonationLog,
    EffectiveSession,
} from "../utils/impersonation.types";

export const RECORDING_IN_OTHER_TAB_ERROR = "A recording is in progress in another tab. Finish it first.";
/** EN-1313: fired when a scheduled stop teardown is abandoned because a recording or upload is in progress. */
export const IMPERSONATION_STOP_ABANDONED_EVENT = "impersonation-stop-abandoned";

/**
 * Impersonation Service
 * Handles all impersonation-related operations including user search,
 * session management, and audit logging
 */

export class ImpersonationService {
    /**
     * EN-837 — single-slot scheduler for the deferred stop-impersonation teardown, shared across
     * calls. The banner + Stop button stay live until the deferred teardown runs (impersonation-changed
     * only fires inside it), so Stop can be clicked repeatedly mid-recording; coalescing collapses those
     * into ONE pending teardown (see stopImpersonation) instead of stacking cache-clears/navigations,
     * each with its own store subscription. Lazily created because recorderActivity is dynamically
     * imported to keep it out of this lib's static import graph.
     */
    private static stopTeardownRunner: CoalescingIdleRunner | null = null;

    /**
     * EN-837 — re-entry guard for the stop teardown. The coalescing runner only supersedes a PENDING
     * idle subscription; in the IDLE path the teardown runs synchronously on schedule, so there's no
     * pending item to supersede. Between two rapid Stop clicks the session is still in localStorage
     * (the teardown clears it via an async IIFE that hasn't run/settled yet), so a naive second call
     * sees an active session and starts a PARALLEL teardown — double clearAllCachedData + double nav +
     * a duplicate audit-log RPC. This flag, set synchronously before the first `await`, collapses
     * repeat clicks to one teardown in both the idle and deferred paths. Reset on a failed attempt
     * (retry) and on a fresh startImpersonation (new session in the same page life).
     */
    private static stopTeardownScheduled = false;

    /**
     * Check if the current user is an admin
     */
    static async isAdmin(): Promise<boolean> {
        try {
            // First check if user is authenticated
            const {
                data: { user },
                error: userError,
            } = await supabase.auth.getUser();

            if (userError || !user) {
                // User is not authenticated, return false without making RPC call
                return false;
            }

            const { data, error } = await supabase.rpc("validate_admin_permissions", {
                admin_user_id: user.id,
            });

            if (error) {
                console.error(
                    "Error validating admin permissions:",
                    error instanceof Error ? error.message : JSON.stringify(error),
                );
                return false;
            }

            return data === true;
        } catch (error) {
            console.error("Error checking admin status:", error);
            return false;
        }
    }

    /**
     * Search for users that can be impersonated
     */
    static async searchUsers(searchTerm: string = "", limit: number = 50): Promise<ImpersonationUser[]> {
        try {
            // First check if user is authenticated
            const {
                data: { user },
                error: userError,
            } = await supabase.auth.getUser();

            if (userError || !user) {
                // User is not authenticated, return empty array without making RPC call
                return [];
            }

            const { data, error } = await supabase.rpc("get_users_for_impersonation", {
                search_term: searchTerm,
                limit_count: limit,
            });
            if (error) {
                console.error("Error searching users:", error);
                return [];
            }

            return data || [];
        } catch (error) {
            console.error("Error in searchUsers:", error);
            return [];
        }
    }

    /**
     * Start impersonating a user
     */
    static async startImpersonation(
        targetUserId: string,
        // Email from the search result. Corporate group users (EN-834) have no `account` row,
        // so the account lookup below finds nothing — we fall back to this hint. Regular clinic
        // users always have an account row, so their behavior is unchanged (the row wins).
        targetEmailHint?: string,
    ): Promise<{ success: boolean; error?: string }> {
        try {
            // EN-1313: starting swaps the identity every request is sent as, and AccountProvider hides
            // the previous identity's account until the new one loads, which remounts the app shell.
            // Mid-recording that would tear down the capture and flush its upload under the wrong
            // identity, the same hazard EN-837 guards against on stop. Refuse before any side effect.
            // Dynamic import keeps this widely-imported lib clear of recorderActivity's static store chain,
            // as in stopImpersonation.
            const { isRecorderActive } = await import("@common/lib/recorderActivity");
            if (isRecorderActive()) {
                return {
                    success: false,
                    error: "Finish the current recording or upload before starting impersonation.",
                };
            }
            // The session change also reaches every other tab (storage event), where it masks the account
            // and remounts that tab's shell. isRecorderActive() only sees this tab, so ask the others too.
            // The liveness responder answers only during live capture, not during an upload. This is a
            // best-effort, 250 ms check (defense in depth), not a guarantee: it is repeated right before
            // the session write below, where it matters, and the same on every stop path.
            if (await isRecordingActiveElsewhere()) {
                return { success: false, error: RECORDING_IN_OTHER_TAB_ERROR };
            }

            // Get current session
            const {
                data: { session },
                error: sessionError,
            } = await supabase.auth.getSession();

            if (sessionError || !session) {
                return { success: false, error: "No active session found" };
            }
            // Get target user details from public account table. Use maybeSingle: a missing row is
            // expected for group users (they aren't clinic accounts) — that returns { data: null,
            // error: null } and must not be treated as an error. A non-null error, however, is a real
            // failure (network, RLS, etc.) and must NOT silently fall through to the email hint —
            // otherwise a transient DB error would be masked and logged with the hinted email.
            const { data: targetUser, error: accountError } = await supabase
                .from("account")
                .select("auth_email")
                .eq("id", targetUserId)
                .maybeSingle();

            if (accountError) {
                console.error("Error fetching target user account:", accountError);
                return { success: false, error: `Account lookup failed: ${accountError.message}` };
            }

            const targetEmail = targetUser?.auth_email ?? targetEmailHint ?? null;

            if (!targetEmail) {
                return {
                    success: false,
                    error: "Target user not found: no account row and no email provided",
                };
            }

            // Log the impersonation start
            const { error: logError } = await supabase.rpc("log_impersonation_activity", {
                p_admin_user_id: session.user.id,
                p_admin_email: session.user.email || "",
                p_target_user_id: targetUserId,
                p_target_email: targetEmail,
                p_action_type: "start",
            });

            if (logError) {
                console.error("Error logging impersonation start:", logError);
                return { success: false, error: "Failed to log impersonation activity" };
            }

            // EN-1313: loaded before the final check so the selected-doctor clear below runs in the same
            // synchronous step as the session write, with no await in between.
            const store = (await import("@/app/store")).default;
            const { clearDoctorState } = await import("@/state/redux/doctorSlice");

            // EN-1313: the checks at the top ran before getSession, the account lookup and the audit RPC.
            // A recording started in this or another tab during those round trips would be remounted by
            // the session write, so check again here. The other-tab await comes first and this tab is read
            // synchronously after it, so nothing can start between the last check and the write. A start
            // refused here has already logged its audit row, the same accepted trade-off as stop.
            if (await isRecordingActiveElsewhere()) {
                return { success: false, error: RECORDING_IN_OTHER_TAB_ERROR };
            }
            if (isRecorderActive()) {
                return {
                    success: false,
                    error: "Finish the current recording or upload before starting impersonation.",
                };
            }

            // Create impersonation session data
            const impersonationSession: ImpersonationSession = {
                isImpersonating: true,
                adminUserId: session.user.id,
                adminEmail: session.user.email || "",
                targetUserId: targetUserId,
                targetEmail: targetEmail,
                impersonationStartTime: new Date().toISOString(),
            };

            // Store impersonation session in localStorage
            localStorage.setItem("impersonation_session", JSON.stringify(impersonationSession));
            // EN-1313: the previous identity's selected doctor belongs to the previous clinic. Without a
            // reload nothing else clears it, and a document toggle would then write doctor_document_types
            // for that doctor. Stop clears it too, via clearAllCachedData before its hard navigation.
            store.dispatch(clearDoctorState());

            // Fresh impersonation → re-arm the stop guard so a later Stop can schedule, covering the rare
            // case where a prior deferred stop never ran (its recording never ended) without a reload. The
            // cancel below is a safety net: a stop is only deferred while the recorder is busy, and start is
            // refused while it is busy (EN-1313, top of this method), so no deferred stop should be armed
            // here. If one ever were, the flag reset alone would not disarm it, and it would fire at idle
            // and clobber THIS new session (clear it, wipe caches, nav home). (EN-837 / Bugbot)
            this.stopTeardownScheduled = false;
            this.stopTeardownRunner?.cancel();

            // Dispatch custom event to notify components of impersonation change
            window.dispatchEvent(new CustomEvent("impersonation-changed"));

            // Update the session with impersonation data
            // Note: In a real implementation, you would need to create a custom JWT
            // For now, we'll use localStorage to track impersonation state

            return { success: true };
        } catch (error) {
            console.error("Error starting impersonation:", error);
            return { success: false, error: "Failed to start impersonation" };
        }
    }

    /**
     * Stop impersonating and return to admin user
     */
    static async stopImpersonation(): Promise<{ success: boolean; error?: string }> {
        try {
            const impersonationSession = this.getImpersonationSession();

            if (!impersonationSession || !impersonationSession.isImpersonating) {
                return { success: false, error: "No active impersonation session" };
            }

            // Re-entry guard (set synchronously before the first await): a stop is already scheduled or
            // in flight: deferred while recording, a rapid double-click in the idle path where
            // the first teardown's async IIFE hasn't cleared the session yet, or still checking other tabs
            // below (that first call reports its own refusal). Don't schedule a second teardown or re-log.
            // (EN-837 / Bugbot)
            if (this.stopTeardownScheduled) {
                return { success: true };
            }
            this.stopTeardownScheduled = true;

            // EN-1313: when the teardown below clears the session, other tabs see it at once (storage
            // event), with no wait for their own recorder, and there it masks the account and remounts the
            // shell. So refuse while another tab is recording or paused (not during an upload, same limit
            // as startImpersonation). A teardown deferred until this tab is idle checks again when it runs.
            if (await isRecordingActiveElsewhere()) {
                this.stopTeardownScheduled = false;
                return { success: false, error: RECORDING_IN_OTHER_TAB_ERROR };
            }

            // Log the impersonation stop
            const { error: logError } = await supabase.rpc("log_impersonation_activity", {
                p_admin_user_id: impersonationSession.adminUserId || "",
                p_admin_email: impersonationSession.adminEmail || "",
                p_target_user_id: impersonationSession.targetUserId!,
                p_target_email: impersonationSession.targetEmail!,
                p_action_type: "stop",
            });

            if (logError) {
                console.error("Error logging impersonation stop:", logError);
                // Continue with stopping impersonation even if logging fails
            }

            // EN-837: the entire teardown below — NOT just the final navigation — tears down an
            // in-flight recording. Clearing the impersonation session makes getEffectiveSession
            // revert to the admin, so an upload still in flight would flush under the WRONG identity;
            // clearAllCachedData wipes the Redux store the recorder relies on. Deferring only the
            // reload (the previous fix) left both exposed. So defer the WHOLE destructive sequence
            // (clear session → notify → clear caches → navigate) until the recorder is idle. When
            // idle it runs immediately. The navigation is owned here (a single, deferred nav home)
            // so callers don't schedule their own and race this one.
            //
            // Route through a single-slot coalescing runner (shared static field): the banner + Stop
            // button stay live until this teardown actually runs, so repeat Stop clicks mid-recording
            // would otherwise each schedule a full teardown — N cache-clears + N navigations that all
            // fire when the recorder idles, each leaking a store subscription. Coalescing supersedes any
            // still-pending teardown so only one survives. Dynamic import keeps this widely-imported lib
            // clear of recorderActivity's static store → supabaseClient chain (which breaks jest
            // without a Supabase mock).
            const { createCoalescingIdleRunner, hasAudioInFlight } = await import("@common/lib/recorderActivity");
            const store = (await import("@/app/store")).default;
            if (!this.stopTeardownRunner) {
                this.stopTeardownRunner = createCoalescingIdleRunner();
            }

            // EN-1313: identifies the session this stop was asked to end (see the superseded check below).
            const stoppingStartTime = impersonationSession.impersonationStartTime;
            this.stopTeardownRunner.schedule(() => {
                void (async () => {
                    // EN-1313: the check above ran before the audit RPC and imports (idle path) or possibly
                    // minutes earlier (deferred path), and another tab may have started recording since.
                    // Check again right before clearing the session, on every path. This tab is re-checked
                    // after that await with hasAudioInFlight (live capture or upload), not isRecorderActive:
                    // the runner deliberately releases a teardown held only by the bounded `completing` /
                    // `finalizing` flags, and re-reading those here would make a stuck flag block Stop for
                    // good. On abandon, re-arm Stop; the banner stays up so the admin can stop again.
                    // A throw from the cross-tab check (it sits outside the try below) would leave the
                    // re-entry flag set with no navigation, so every later Stop would no-op. Treat it like
                    // "no other tab answered", which is also its answer when BroadcastChannel is unavailable.
                    const recordingElsewhere = await isRecordingActiveElsewhere().catch(() => false);
                    if (recordingElsewhere || hasAudioInFlight(store.getState().globalState)) {
                        this.stopTeardownScheduled = false;
                        console.warn("Impersonation stop abandoned: a recording or upload is in progress.");
                        // The banner stays up either way; tell the admin why, so they know to stop again.
                        window.dispatchEvent(new CustomEvent(IMPERSONATION_STOP_ABANDONED_EVENT));
                        return;
                    }
                    // cancel() in startImpersonation cannot abort a teardown already awaiting the check above.
                    // If a newer session was written meanwhile (this tab's start, or another tab's), this stop is
                    // superseded: back off rather than delete it and navigate away, and re-arm Stop, since a
                    // session started in another tab never ran this tab's startImpersonation to reset the flag.
                    // No session at all (another tab already stopped) is not superseded: finish the teardown so
                    // this tab drops the impersonated user's cached data. Read synchronously, right before the clear.
                    const currentSession = this.getImpersonationSession();
                    if (currentSession && currentSession.impersonationStartTime !== stoppingStartTime) {
                        this.stopTeardownScheduled = false;
                        console.warn("Impersonation stop superseded by a newer impersonation session.");
                        return;
                    }
                    // Any step here can throw (storage disabled, cache-clear failure). Wrap so a
                    // throw can't strand the admin mid-teardown (session cleared, caches half-wiped,
                    // banner still showing) with the page never navigating home. The navigation is
                    // the one non-negotiable outcome, so it lives in `finally`.
                    try {
                        // Clear impersonation session first
                        localStorage.removeItem("impersonation_session");

                        // Notify components (banner, AccountProvider, etc.) in the same task as the
                        // localStorage change. apiFetch reads identity from localStorage, so if React only
                        // learned about the stop after the await below, requests would pair the admin
                        // identity with the target's clinic id for that whole window (EN-1313).
                        window.dispatchEvent(new CustomEvent("impersonation-changed"));

                        // Clear all cached data after stopping impersonation. Await before navigating so
                        // impersonated-user data is gone before the page reloads (replaces the previous
                        // best-effort 200ms delay).
                        await this.clearAllCachedData();
                    } catch (teardownError) {
                        Sentry.captureException(teardownError, {
                            tags: { errorCategory: "auth", operation: "stopImpersonation.teardown" },
                        });
                    } finally {
                        // Force a full navigation home for a clean admin-identity state — always.
                        window.location.href = "/";
                    }
                })();
            });

            return { success: true };
        } catch (error) {
            // A failed attempt never scheduled a teardown — re-arm so the admin can retry Stop.
            this.stopTeardownScheduled = false;
            // EN-837: this try now also wraps `await import("recorderActivity")`, which can fail with a
            // chunk-load error after a Vercel redeploy invalidates the old chunk hash on a still-open tab.
            // If we only console.error here, Stop silently no-ops and the admin stays impersonating with
            // no telemetry — a real footgun given cross-clinic RLS. Report so it's findable; the caller
            // (ImpersonationBanner) surfaces a toast on the returned failure.
            console.error("Error stopping impersonation:", error);
            Sentry.captureException(error, {
                tags: { errorCategory: "auth", operation: "stopImpersonation" },
            });
            return { success: false, error: "Failed to stop impersonation" };
        }
    }

    /**
     * Get current impersonation session
     */
    static getImpersonationSession(): ImpersonationSession | null {
        try {
            const sessionData = localStorage.getItem("impersonation_session");
            if (!sessionData) {
                return null;
            }

            return JSON.parse(sessionData) as ImpersonationSession;
        } catch (error) {
            console.error("Error parsing impersonation session:", error);
            return null;
        }
    }

    /**
     * Check if currently impersonating
     */
    static isCurrentlyImpersonating(): boolean {
        const session = this.getImpersonationSession();
        return session?.isImpersonating === true;
    }

    /**
     * Get impersonation logs for admin users
     */
    static async getImpersonationLogs(limit: number = 100): Promise<ImpersonationLog[]> {
        try {
            // First check if user is authenticated
            const {
                data: { user },
                error: userError,
            } = await supabase.auth.getUser();

            if (userError || !user) {
                // User is not authenticated, return empty array without making database call
                return [];
            }

            const { data, error } = await supabase
                .from("impersonation_logs")
                .select("*")
                .order("created_at", { ascending: false })
                .limit(limit);

            if (error) {
                console.error("Error fetching impersonation logs:", error);
                return [];
            }

            return (data || []) as ImpersonationLog[];
        } catch (error) {
            console.error("Error in getImpersonationLogs:", error);
            return [];
        }
    }

    /**
     * Get the effective user ID (impersonated user if impersonating, otherwise current user)
     */
    static async getEffectiveUserId(): Promise<string | null> {
        const impersonationSession = this.getImpersonationSession();

        if (impersonationSession?.isImpersonating && impersonationSession.targetUserId) {
            return Promise.resolve(impersonationSession.targetUserId);
        }

        // Return current user ID from session
        return supabase.auth.getUser().then((result) => result.data.user?.id || null);
    }

    /**
     * Get the effective user email (impersonated user if impersonating, otherwise current user)
     */
    static async getEffectiveUserEmail(): Promise<string | null> {
        const impersonationSession = this.getImpersonationSession();

        if (impersonationSession?.isImpersonating && impersonationSession.targetEmail) {
            return Promise.resolve(impersonationSession.targetEmail);
        }

        // Return current user email from session
        return supabase.auth.getUser().then((result) => result.data.user?.email || null);
    }

    /**
     * Get the effective session for API calls (impersonated user if impersonating, otherwise current user)
     * This returns the session data that should be used for API calls
     */
    static async getEffectiveSession(): Promise<EffectiveSession | null> {
        try {
            const {
                data: { session },
                error: sessionError,
            } = await supabase.auth.getSession();

            if (sessionError || !session) {
                return null;
            }

            const impersonationSession = this.getImpersonationSession();

            if (impersonationSession?.isImpersonating && impersonationSession.targetUserId) {
                // When impersonating, we need to create a modified session object
                // that uses the impersonated user's ID but keeps the admin's access token
                // The server will need to handle impersonation based on additional headers
                return {
                    session: {
                        ...session,
                        user: {
                            ...session.user,
                            id: impersonationSession.targetUserId,
                            email: impersonationSession.targetEmail ?? undefined,
                        },
                    },
                    userId: impersonationSession.targetUserId,
                    accessToken: session.access_token,
                    isImpersonating: true,
                };
            }

            // Not impersonating, return normal session
            return {
                session,
                userId: session.user.id,
                accessToken: session.access_token,
                isImpersonating: false,
            };
        } catch (error) {
            console.error("Error getting effective session:", error);
            return null;
        }
    }

    /**
     * Clear all cached data when stopping impersonation
     * This ensures that impersonated user's data doesn't persist after stopping impersonation
     */
    static async clearAllCachedData(): Promise<void> {
        try {
            // Clear Redux store data by dispatching reset actions
            // We need to dynamically import the store and actions to avoid circular dependencies
            const store = (await import("@/app/store")).default;

            // Clear patient records
            const { clearTableRecords } = await import("@/state/redux/patientTableRecordsSlice");
            store.dispatch(clearTableRecords());

            // Clear doctors data
            const { clearDoctorState } = await import("@/state/redux/doctorSlice");
            store.dispatch(clearDoctorState());

            // Clear resources data
            const { clearResourceState } = await import("@/state/redux/resourcesSlice");
            store.dispatch(clearResourceState());

            // Clear documents data
            const { clearDocumentState } = await import("@/state/redux/documentSlice");
            store.dispatch(clearDocumentState());

            // Clear any other cached data in localStorage that might be user-specific
            const keysToRemove = [
                "selectedDoctor",
                "selectedDocuments",
                "historyDocuments",
                "incompleteUploads",
                "sectionsAndFieldsToBold",
            ];

            keysToRemove.forEach((key) => {
                localStorage.removeItem(key);
            });

            // Clear any cached account data
            localStorage.removeItem("accountData");
            localStorage.removeItem("communicationsData");
            localStorage.removeItem("pmsIntegrationData");

            // Force clear any remaining cached data
            sessionStorage.clear();
        } catch (error) {
            console.error("Error clearing cached data:", error);
            // Deliberately swallowed — a cache-clear failure must not block navigating home. But report
            // it: a partial wipe can leave impersonated-user data (localStorage / persisted cache) alive
            // into the admin's session — a cross-identity leak that's otherwise invisible in Sentry.
            // (EN-837) Because this swallows, the stopImpersonation teardown's own catch won't see a
            // cache-clear failure; this is where that failure becomes findable.
            Sentry.captureException(error, {
                tags: { errorCategory: "auth", operation: "stopImpersonation.clearCache" },
            });
            // Don't throw error as this shouldn't prevent impersonation from stopping
        }
    }
}
